import { Link } from "react-router-dom";

import { SectionHeader } from "@/components/ui";
import { t } from "@/i18n";
import { fmtInt } from "@/lib/utils";

interface SghvDonem {
  status?: string;
  sghv_genel?: number;
  donem_satilan_toplam_adet?: number;
  aktif_kart_sayisi?: number;
  ortalama_stok_devir_gun?: number | null;
  yavas_hareket_eden_kart_sayisi?: number;
  hizli_hareket_eden_kart_sayisi?: number;
  thresholds?: { yavas_esik?: number; hizli_esik?: number };
}

interface Kart {
  key: string;
  label: string;
  value: string;
  sub: string;
  tone: "kpi-ok" | "kpi-warn" | "kpi-bad" | "";
}

function donemOku(sghv: unknown): SghvDonem | null {
  if (!sghv || typeof sghv !== "object") return null;
  const s = sghv as { error?: unknown; bu_ay?: unknown };
  if (s.error) return null;
  const buAy = s.bu_ay;
  return buAy && typeof buAy === "object" ? (buAy as SghvDonem) : null;
}

/** "4079 gün" gibi anlamsiz uzunluklar yerine okunur etiket. */
function devirSuresi(gun: number | null | undefined): { value: string; sub: string } {
  if (gun == null || !Number.isFinite(gun)) return { value: "—", sub: t("sghv.gun") };
  if (gun >= 365) return { value: t("sghv.bir_yildan_uzun"), sub: t("sghv.devir_yavas_not") };
  return { value: `${Math.round(gun)} ${t("sghv.gun")}`, sub: t("sghv.devir_not") };
}

/**
 * Stok Devir Hızı (SGHV) — 4 kart: Genel / Ortalama devir süresi / Yavaş /
 * Hızlı hareket (son 30 gün). Alt satirlar yoneticinin okuyabilecegi dilde.
 */
export function SghvKartlari({ sghv }: { sghv: unknown }) {
  const d = donemOku(sghv);
  const yok = d === null || d.status === "data_insufficient";
  const yavasEsik = d?.thresholds?.yavas_esik ?? 0.5;
  const hizliEsik = d?.thresholds?.hizli_esik ?? 5;
  const genel = Number(d?.sghv_genel ?? 0);
  const devir = devirSuresi(d?.ortalama_stok_devir_gun);
  const yavas = d?.yavas_hareket_eden_kart_sayisi ?? 0;
  const aktif = d?.aktif_kart_sayisi ?? 0;
  const kartlar: Kart[] = yok
    ? [
        { key: "genel", label: t("sghv.genel"), value: "—", sub: d === null ? t("sghv.veri_yok") : t("sghv.veri_yetersiz"), tone: "" },
        { key: "ortalama", label: t("sghv.ortalama_gun"), value: "—", sub: t("sghv.gun"), tone: "" },
        { key: "yavas", label: t("sghv.yavas"), value: "—", sub: t("sghv.kart"), tone: "" },
        { key: "hizli", label: t("sghv.hizli"), value: "—", sub: t("sghv.kart"), tone: "" },
      ]
    : [
        {
          key: "genel",
          label: t("sghv.genel"),
          value: genel.toLocaleString("tr-TR", { maximumFractionDigits: 2 }),
          sub: t("sghv.genel_alt", { adet: fmtInt(d.donem_satilan_toplam_adet ?? 0), kart: fmtInt(aktif) }),
          tone: genel >= 1 ? "kpi-ok" : genel >= 0.3 ? "kpi-warn" : "kpi-bad",
        },
        { key: "ortalama", label: t("sghv.ortalama_gun"), value: devir.value, sub: devir.sub, tone: "" },
        {
          key: "yavas",
          label: t("sghv.yavas"),
          value: fmtInt(yavas),
          sub: t("sghv.yavas_alt", { esik: String(yavasEsik) }),
          tone: aktif > 0 && yavas / aktif > 0.5 ? "kpi-warn" : "kpi-ok",
        },
        {
          key: "hizli",
          label: t("sghv.hizli"),
          value: fmtInt(d.hizli_hareket_eden_kart_sayisi ?? 0),
          sub: t("sghv.hizli_alt", { esik: String(hizliEsik) }),
          tone: "kpi-ok",
        },
      ];

  return (
    <div data-testid="sghv-kartlari">
      <SectionHeader
        title={t("sghv.baslik")}
        note={
          <>
            {t("sghv.not")}{" "}
            <Link to="/stok" className="font-semibold text-brand-primary hover:underline">
              {t("sghv.stok_link")}
            </Link>
          </>
        }
      />
      <div className="mt-3 grid grid-cols-2 gap-3 xl:grid-cols-4">
        {kartlar.map((k) => (
          <div key={k.key} className={`kpi ${k.tone || "kpi-off"}`} data-testid={`sghv-${k.key}`}>
            <p className="kpi-lbl">{k.label}</p>
            <p className="kpi-val mt-2 text-[24px]">{k.value}</p>
            <p className="kpi-sub">{k.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
